import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import { buildApiUrl } from "../../config/api";
import { getSchoolIdForUser } from "../../utils/userContext";
import { fetchManebDashboard } from "../../utils/manebApi";

const TeacherManebPage = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [examTimetable, setExamTimetable] = useState([]);

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const dash = await fetchManebDashboard(schoolId);
            setSessions((dash.sessions || []).filter((s) => s.status === "Open"));
            const { data: cal } = await axios.get(buildApiUrl(`/SchoolCalendar/${schoolId}`));
            setExamTimetable(cal?.examTimetable || cal?.examSchedule || []);
        } catch {
            setSessions([]);
            setExamTimetable([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        MANEB examinations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View published exam timetable and open mock sessions for your exam classes.
                    </Typography>
                </Box>

                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <CalendarMonthOutlinedIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Published exam timetable
                                </Typography>
                            </Stack>
                            {examTimetable.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No published exam dates yet. The director builds these under Admin → Calendar.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Class</TableCell>
                                            <TableCell>Subject / paper</TableCell>
                                            <TableCell>Session</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {examTimetable.slice(0, 20).map((row, i) => (
                                            <TableRow key={row.id || i}>
                                                <TableCell>{row.date || row.examDate || "—"}</TableCell>
                                                <TableCell>{row.className || row.sclassName || "—"}</TableCell>
                                                <TableCell>{row.subject || row.title || "—"}</TableCell>
                                                <TableCell>{row.session || row.timeSlot || "—"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            <Button component={RouterLink} to="/Teacher/timetable" size="small" sx={{ mt: 1 }}>
                                Weekly timetable
                            </Button>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <GradingIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Open mock sessions
                                </Typography>
                            </Stack>
                            {sessions.length === 0 ? (
                                <Alert severity="info">No open MANEB mock sessions. The director creates these from Admin → MANEB.</Alert>
                            ) : (
                                <Stack spacing={1}>
                                    {sessions.map((s) => (
                                        <Box key={s.id || s._id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Box>
                                                <Typography fontWeight={600}>{s.title}</Typography>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Chip label={s.examType} size="small" />
                                                    <Chip label={s.sessionKind} size="small" variant="outlined" />
                                                </Stack>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Ask director to enter marks in session manager, or use Marks entry with label &quot;Mock exam&quot;
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </>
                )}
            </Stack>
        </Container>
    );
};

export default TeacherManebPage;
